// src/auth/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../services/apiClient';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessToken, setAccessToken] = useState(localStorage.getItem('access_token'));
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalUser, setOriginalUser] = useState(null);
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const { i18n } = useTranslation();

  // Extract token from URL fragment (after Auth0 callback)
  const extractTokenFromUrl = () => {
    const hash = window.location.hash;
    
    if (hash) {
      try {
        const params = new URLSearchParams(hash.substring(1));
        const token = params.get('access_token');
        const tokenType = params.get('token_type');
        
        
        if (token && tokenType === 'Bearer') {
          console.log('Valid token found, storing in localStorage');
          // Store token
          localStorage.setItem('access_token', token);
          setAccessToken(token);
          
          // Clean up URL
          window.history.replaceState({}, document.title, window.location.pathname);
          
          return token;
        } else {
          console.warn('Invalid token or token type:', { 
            token: token ? 'present' : 'missing', 
            tokenType,
            fullHash: hash
          });
        }
      } catch (error) {
        console.error('Error parsing URL hash:', error);
      }
    } 
    return null;
  };

  // Check if token is expired
  const isTokenExpired = (token) => {
    if (!token) return true;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return true;
    }
  };

  // Clear authentication data
  const clearAuth = () => {
    setUser(null);
    setAccessToken(null);
    setIsImpersonating(false);
    setOriginalUser(null);
    setImpersonatedUser(null);
    setIsAdmin(false);
    localStorage.removeItem('access_token');
  };

  // Authenticated fetch wrapper
  const authenticatedFetch = async (url, options = {}) => {
    const token = accessToken || localStorage.getItem('access_token');
    
    if (!token || isTokenExpired(token)) {
      clearAuth();
      throw new Error('No valid authentication token');
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      clearAuth();
      throw new Error('Authentication failed');
    }

    return response;
  };

  // Get user info from token
  const getUserFromToken = (token) => {
    if (!token) return null;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        email: payload.email,
        name: payload.name,
        picture: payload.picture,
        preferences: payload.preferences || {},
        group: payload.group,
        clinic: payload.clinic,
        subscription: payload.subscription,
        flics: payload.flics,
        isImpersonated: payload.isImpersonated || false,
        originalAdminEmail: payload.originalAdminEmail || null
      };
    } catch (error) {
      console.error('Error parsing token:', error);
      return null;
    }
  };

  // Update user preferences
  const updateUserPreferences = async (preferences) => {
    try {
      const response = await apiClient.patch('/api/profile/preferences', {
        preferences
      });
      
      if (response) {
        // Update local user state with new preferences
        setUser(prev => ({
          ...prev,
          preferences: response.preferences
        }));

        // Update token if provided in response
        if (response.token) {
          localStorage.setItem('access_token', response.token);
          setAccessToken(response.token);
        }

        return response;
      }
    } catch (error) {
      console.error('Error updating preferences:', error);
      throw error;
    }
  };

  // Check admin status
  const checkAdminStatus = async () => {
    try {
      const response = await apiClient.get('/api/admin/check-admin');
      if (response && response.isAdmin) {
        setIsAdmin(true);
        return true;
      }
      setIsAdmin(false);
      return false;
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
      return false;
    }
  };

  // Fetch users for impersonation
  const fetchUsers = async () => {
    try {
      const response = await apiClient.get('/api/admin/users');
      return Array.isArray(response) ? response : [];
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  };

  // Start impersonation
  const startImpersonation = async (userId) => {
    try {
      // Store current user as original
      if (!originalUser && user) {
        setOriginalUser(user);
      }

      // Call API to start impersonation
      const response = await apiClient.post('/api/admin/impersonate', { userId });
      
      if (response && response.token) {
        // Update token
        localStorage.setItem('access_token', response.token);
        setAccessToken(response.token);

        // Get impersonated user from new token
        const impersonatedUserData = getUserFromToken(response.token);
        if (impersonatedUserData) {
          setImpersonatedUser(impersonatedUserData);
          setUser(impersonatedUserData);
          setIsImpersonating(true);
          
          // Update language preference if needed
          const userLanguage = impersonatedUserData.preferences?.language;
          if (userLanguage) {
            i18n.changeLanguage(userLanguage);
            localStorage.setItem('preferredLanguage', userLanguage);
          }
        }
        return response;
      }
      throw new Error('Invalid response from impersonation endpoint');
    } catch (error) {
      console.error('Error starting impersonation:', error);
      throw error;
    }
  };

  // Stop impersonation
  const stopImpersonation = async () => {
    try {
      const response = await apiClient.post('/api/admin/stop-impersonate');
      
      if (response && response.token) {
        // Restore original token
        localStorage.setItem('access_token', response.token);
        setAccessToken(response.token);

        // Restore original user
        if (originalUser) {
          setUser(originalUser);
          setImpersonatedUser(null);
          setIsImpersonating(false);
          
          // Restore language preference
          const userLanguage = originalUser.preferences?.language;
          if (userLanguage) {
            i18n.changeLanguage(userLanguage);
            localStorage.setItem('preferredLanguage', userLanguage);
          }
        } else {
          // Fallback: get user from token
          const userFromToken = getUserFromToken(response.token);
          if (userFromToken) {
            setUser(userFromToken);
            setImpersonatedUser(null);
            setIsImpersonating(false);
          }
        }
        return response;
      }
      throw new Error('Invalid response from stop impersonation endpoint');
    } catch (error) {
      console.error('Error stopping impersonation:', error);
      throw error;
    }
  };

  // Check authentication status
  const checkAuth = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // First, check if there's a token in the URL (from Auth0 callback)
      let token = extractTokenFromUrl();
      
      // If no token in URL, check localStorage
      if (!token) {
        token = localStorage.getItem('access_token');
      }

      if (!token || isTokenExpired(token)) {
        clearAuth();
        return;
      }

      // Get user info from token
      const userFromToken = getUserFromToken(token);
      if (userFromToken) {
        setUser(userFromToken);
        setAccessToken(token);
        
        // Check if impersonating
        if (userFromToken.isImpersonated) {
          setIsImpersonating(true);
          setImpersonatedUser(userFromToken);
          // Original user email is in the token
          if (userFromToken.originalAdminEmail && !originalUser) {
            // We don't have full original user data, but we know we're impersonating
            setOriginalUser({ email: userFromToken.originalAdminEmail });
          }
        } else {
          setIsImpersonating(false);
          setImpersonatedUser(null);
        }
        
        // Set language preference from user preferences or localStorage
        const userLanguage = userFromToken.preferences?.language;
        if (userLanguage) {
          i18n.changeLanguage(userLanguage);
          localStorage.setItem('preferredLanguage', userLanguage);
        } else {
          // If no user preference, try to get from localStorage
          const storedLanguage = localStorage.getItem('preferredLanguage');
          if (storedLanguage) {
            i18n.changeLanguage(storedLanguage);
          }
        }

        // Check admin status after user is loaded
        await checkAdminStatus();
      } else {
        clearAuth();
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setError(error.message);
      clearAuth();
    } finally {
      setIsLoading(false);
    }
  };

  // Login - redirect to backend login endpoint
  const login = () => {
    const loginUrl = new URL(`${process.env.REACT_APP_API_BASE_URL}/api/auth/login`);
    loginUrl.searchParams.set('returnTo', `${process.env.REACT_APP_FRONTEND_URL}/consultation`);
    
    window.location.href = loginUrl.toString();
  };

  // Logout
  const logout = async () => {
    try {
      // Set post-logout flag
      window.sessionStorage.setItem('post_logout', 'true');
      
      // Clear all auth data
      clearAuth();
      
      // Clear any potential session cookies
      document.cookie.split(";").forEach(function(c) { 
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });
      
      // Redirect to backend logout endpoint which handles Auth0 logout
      window.location.href = `${process.env.REACT_APP_API_BASE_URL}/api/auth/logout?returnTo=${encodeURIComponent(window.location.origin)}`;
    } catch (error) {
      console.error('Logout failed:', error);
      setError(error.message);
    }
  };

  // Check auth on mount and when URL changes
  useEffect(() => {
    checkAuth();
  }, []);

  // Set up token expiration checking
  useEffect(() => {
    if (accessToken && !isTokenExpired(accessToken)) {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      const expirationTime = payload.exp * 1000; // Convert to milliseconds
      const timeUntilExpiration = expirationTime - Date.now();
      
      if (timeUntilExpiration > 0) {
        const timeout = setTimeout(() => {
          console.log('Token expired, clearing auth');
          clearAuth();
        }, timeUntilExpiration);
        
        return () => clearTimeout(timeout);
      } else {
        clearAuth();
      }
    }
  }, [accessToken]);

  const value = {
    user,
    isLoading,
    error,
    login,
    logout,
    updateUserPreferences,
    authenticatedFetch,
    isAuthenticated: !!user && !!accessToken && !isTokenExpired(accessToken),
    // Impersonation
    isImpersonating,
    originalUser,
    impersonatedUser,
    isAdmin,
    checkAdminStatus,
    fetchUsers,
    startImpersonation,
    stopImpersonation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 